import type { FC, ReactNode } from 'react'
import type { LayoutProps } from '@/types'

import { useEffect } from 'react'
import { Header } from '@/components/generals/header'
import { Footer } from '@/components/generals/footer'

/**
 * Sections marked with data-surface tint the whole page background while they
 * sit in the middle of the screen, so the page tone shifts as you scroll.
 */
const useSurfaceShift = (): void => {
    useEffect(() => {
        const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-surface]'))
        if (!sections.length || typeof IntersectionObserver === 'undefined') return

        const active = new Set<HTMLElement>()

        const apply = (): void => {
            const current = Array.from(active).pop()
            if (current) document.body.setAttribute('data-surface', current.dataset.surface ?? '')
            else document.body.removeAttribute('data-surface')
        }

        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const target = entry.target as HTMLElement
                if (entry.isIntersecting) active.add(target)
                else active.delete(target)
            }
            apply()
        }, { rootMargin: '-45% 0px -45% 0px' })

        sections.forEach((section) => observer.observe(section))

        return () => {
            observer.disconnect()
            document.body.removeAttribute('data-surface')
        }
    }, [])
}

export const Layout: FC<LayoutProps> = ({ children }): ReactNode => {
    useSurfaceShift()

    return (
        <div className='flex min-h-screen flex-col'>
            <a
                href='#main'
                className='sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-ink focus:px-3 focus:py-2 focus:text-sm focus:text-paper'
            >
                Skip to content
            </a>

            <Header />

            <main id='main' className='flex-1'>
                {children}
            </main>

            <Footer />
        </div>
    )
}
